import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableColumn, Pagination, SearchBar, Button } from '@/components/common';
import { Supplier, SupplierTransactionRecord, SupplierDebtSummary } from '@/types/supplier';
import { formatCurrency } from '@/utils/number-format';
import { supplierApi } from '@/api/endpoints/suppliers';
import { DebtAdjustmentVoucherResponse } from '@/types/debtAdjustmentVoucher';
import CreateDebtAdjustmentVoucherModal from '@/components/debtAdjustmentVouchers/modals/CreateDebtAdjustmentVoucherModal';
import ConfirmDebtAdjustmentVoucherModal from '@/components/debtAdjustmentVouchers/modals/ConfirmDebtAdjustmentVoucherModal';
import DateRangePicker from '@/components/customers/DateRangePicker';

interface SupplierDebtTabProps {
  warehouseReceipts?: any[]; // Optional - kept for backward compatibility but not used
  supplier: Supplier | null;
  loading?: boolean; // Optional - kept for backward compatibility but not used
  onDateRangeChange?: (dateFrom: string, dateTo: string) => void;
}

const SupplierDebtTab: React.FC<SupplierDebtTabProps> = ({
  supplier,
  loading: propsLoading, // Rename to avoid conflict
  onDateRangeChange,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  // Date range for filtering (default: current month)
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of month
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  });

  const [dateTo, setDateTo] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  });

  const handleDateRangeChange = (from: string, to: string) => {
    // Always update even if values appear the same to trigger useEffect
    console.log('Date range changed:', { from, to, currentFrom: dateFrom, currentTo: dateTo });
    setDateFrom(from);
    setDateTo(to);
    setCurrentPage(1); // Reset to first page
    onDateRangeChange?.(from, to);
  };

  // Notify parent of initial date range on mount
  useEffect(() => {
    onDateRangeChange?.(dateFrom, dateTo);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debt report state (using unified API)
  const [debtReportLoading, setDebtReportLoading] = useState(false);
  const [transactions, setTransactions] = useState<SupplierTransactionRecord[]>([]);
  const [debtSummary, setDebtSummary] = useState<SupplierDebtSummary | null>(null);

  // Modal states
  const [showCreateDebtAdjustmentModal, setShowCreateDebtAdjustmentModal] = useState(false);
  const [showConfirmDebtAdjustmentModal, setShowConfirmDebtAdjustmentModal] = useState(false);
  const [createdDebtAdjustmentVoucher, setCreatedDebtAdjustmentVoucher] = useState<DebtAdjustmentVoucherResponse | null>(null);

  // Active tab for filtering record types
  const [activeTab, setActiveTab] = useState<'all' | 'receipts' | 'vouchers' | 'adjustments'>('all');

  // Fetch debt report using unified API (similar to CustomerDebtTab)
  useEffect(() => {
    const fetchDebtReport = async () => {
      if (!supplier?.code) {
        setTransactions([]);
        return;
      }

      console.log('Fetching debt report with:', { supplierCode: supplier.code, dateFrom, dateTo, page: currentPage, pageSize });
      setDebtReportLoading(true);
      try {
        // Fetch debt report with transactions
        const response = await supplierApi.getSupplierDebtReport({
          supplierCode: supplier.code,
          dateFrom,
          dateTo,
          page: currentPage,
          pageSize,
        });

        console.log('Debt report response:', response);
        setTransactions(response.data?.data || []);
      } catch (err) {
        console.error('Error fetching debt report:', err);
        setTransactions([]);
      } finally {
        setDebtReportLoading(false);
      }
    };

    fetchDebtReport();
  }, [supplier?.code, currentPage, pageSize, dateFrom, dateTo]); // dateFrom and dateTo are dependencies

  // Fetch debt summary separately (for summary cards)
  useEffect(() => {
    const fetchSummary = async () => {
      if (!supplier?.code) {
        setDebtSummary(null);
        return;
      }

      try {
        const summary = await supplierApi.getSupplierDebtSummary({
          supplierCode: supplier.code,
          dateFrom,
          dateTo,
        });
        setDebtSummary(summary);
      } catch (err) {
        console.error('Error fetching debt summary:', err);
      }
    };

    fetchSummary();
  }, [supplier?.code, dateFrom, dateTo]);





  // Format date to show only day-month-year
  const formatDateOnly = (dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '-';
    }
  };

  const columns: TableColumn<SupplierTransactionRecord>[] = [
    {
      key: 'code',
      title: t('warehouseReceipts.quickView.receiptCode'),
      dataIndex: 'code',
      width: 150,
      sortable: true,
      render: (value) => (
        <div className="font-medium text-primary-600">
          {value}
        </div>
      ),
    },
    {
      key: 'record_type',
      title: t('suppliers.tabs.recordType') || 'Loại',
      dataIndex: 'record_type',
      width: 120,
      render: (value) => {
        switch (value) {
          case 'warehouse_receipt':
            return <span className="text-sm text-gray-700">{t('warehouseReceipts.titleSingle') || 'Phiếu nhập'}</span>;
          case 'payment':
            return <span className="text-sm text-gray-700">{t('paymentVouchers.titleSingle') || 'Phiếu chi'}</span>;
          case 'debt_adjustment':
            return <span className="text-sm text-gray-700">{t('debtAdjustmentVoucher.titleSingle') || 'Phiếu điều chỉnh công nợ'}</span>;
          default:
            return <span className="text-sm text-gray-700">{value}</span>;
        }
      },
    },
    {
      key: 'date',
      title: t('warehouseReceipts.quickView.receiptDate'),
      dataIndex: 'created_at',
      width: 120,
      sortable: true,
      render: (value, record) => {
        let displayDate = value;
        if (record.record_type === 'debt_adjustment') {
          displayDate = record.adjustment_date || value; // Use adjustment_date for debt adjustments
        }
        return (
          <div className="text-sm text-gray-900">{displayDate ? formatDateOnly(displayDate) : '-'}</div>
        );
      },
    },
    {
      key: 'amount',
      title: t('warehouseReceipts.quickView.totalAmount'),
      dataIndex: 'total',
      width: 150,
      align: 'right',
      sortable: true,
      render: (value, record) => {
        // For debt adjustments, show return_amount (which is positive)
        // For warehouse receipts, show total
        // For payments, show total (negative)
        let amount: number;
        if (record.record_type === 'debt_adjustment') {
          amount = record.return_amount || 0; // return_amount is positive for adjustments
        } else {
          amount = value || 0;
        }
        const isAdjustment = record.record_type === 'debt_adjustment';
        const isPayment = record.record_type === 'payment';
        return (
          <div className={`font-medium ${isAdjustment ? 'text-red-600' :
            isPayment ? 'text-green-600' :
              'text-gray-900'
            }`}>
            {formatCurrency(amount)}
          </div>
        );
      },
    },
    {
      key: 'note',
      title: t('warehouseReceipts.quickView.note'),
      dataIndex: 'note',
      render: (value, record) => {
        // For debt adjustments, show reason in note column
        if (record.record_type === 'debt_adjustment') {
          return (
            <div className="text-sm text-gray-500 max-w-xs truncate" title={record.reason}>
              {record.reason || '-'}
            </div>
          );
        }
        return (
          <div className="text-sm text-gray-500 max-w-xs truncate">
            {value || '-'}
          </div>
        );
      },
    },
  ];

  // Filter and paginate transactions based on activeTab and searchQuery
  const filteredAndPaginatedTransactions = useMemo(() => {
    let filtered = transactions;

    // Filter by record type based on activeTab
    if (activeTab === 'receipts') {
      filtered = filtered.filter(t => t.record_type === 'warehouse_receipt');
    } else if (activeTab === 'vouchers') {
      filtered = filtered.filter(t => t.record_type === 'payment');
    } else if (activeTab === 'adjustments') {
      filtered = filtered.filter(t => t.record_type === 'debt_adjustment');
    }
    // 'all' tab shows all records

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(t =>
        t.code.toLowerCase().includes(q) ||
        (t.note || '').toLowerCase().includes(q) ||
        (t.reason || '').toLowerCase().includes(q)
      );
    }

    // Sort by date desc (already sorted by backend, but re-sort for consistency)
    filtered.sort((a, b) => {
      const aDate = a.record_type === 'debt_adjustment' ? (a.adjustment_date || a.created_at) : a.created_at;
      const bDate = b.record_type === 'debt_adjustment' ? (b.adjustment_date || b.created_at) : b.created_at;
      return (bDate || '').localeCompare(aDate || '');
    });

    // Apply pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return {
      data: filtered.slice(startIndex, endIndex),
      total: filtered.length,
    };
  }, [transactions, searchQuery, currentPage, pageSize, activeTab]);

  if (debtReportLoading || propsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }


  const handleCreateDebtAdjustment = () => {
    setShowCreateDebtAdjustmentModal(true);
  };

  const handleDebtAdjustmentCreated = (voucher: DebtAdjustmentVoucherResponse) => {
    setCreatedDebtAdjustmentVoucher(voucher);
    setShowCreateDebtAdjustmentModal(false);
    setShowConfirmDebtAdjustmentModal(true);
  };

  const handleRefreshDebtAdjustments = async () => {
    // Refresh debt report and summary after creating adjustment
    if (!supplier?.code) return;

    // Trigger re-fetch by updating a dependency
    // The useEffect will automatically refetch when dateFrom/dateTo change
    // But we can also manually trigger by calling the API again
    try {
      const response = await supplierApi.getSupplierDebtReport({
        supplierCode: supplier.code,
        dateFrom,
        dateTo,
        page: currentPage,
        pageSize,
      });
      setTransactions(response.data?.data || []);

      const summary = await supplierApi.getSupplierDebtSummary({
        supplierCode: supplier.code,
        dateFrom,
        dateTo,
      });
      setDebtSummary(summary);
    } catch (err) {
      console.error('Error refreshing debt report:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Date Range Picker */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={handleDateRangeChange}
        />
      </div>

      {/* Summary for supplier debt */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(debtSummary?.opening_balance || 0)}
            </div>
            <div className="text-sm text-gray-500">
              {t('suppliers.fields.openingBalance')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(debtSummary?.warehouse_receipt_amount || 0)}
            </div>
            <div className="text-sm text-gray-500">
              {t('suppliers.tabs.totalReceiptsAmount')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(debtSummary?.payment_amount || 0)}
            </div>
            <div className="text-sm text-gray-500">
              {t('suppliers.tabs.paymentAmount') || 'Tổng thanh toán'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(debtSummary?.closing_balance || 0)}
            </div>
            <div className="text-sm text-gray-500">
              {t('suppliers.tabs.totalDebtToPay')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-indigo-600">
              {formatCurrency(debtSummary?.return_amount || 0)}
            </div>
            <div className="text-sm text-gray-500">
              {t('suppliers.tabs.debtAdjustments') || 'Điều chỉnh công nợ'}
            </div>
          </div>
        </div>
        {/* Action Button */}
        <div className="mt-4 flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateDebtAdjustment}
            disabled={!supplier?.code}
          >
            {t('debtAdjustmentVoucher.buttons.create')}
          </Button>
        </div>
      </div>

      {/* Record Type Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'all'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          {t('suppliers.tabs.all')}
        </button>
        <button
          onClick={() => setActiveTab('receipts')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'receipts'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          {t('warehouseReceipts.title')}
        </button>
        <button
          onClick={() => setActiveTab('vouchers')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'vouchers'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          {t('paymentVouchers.title')}
        </button>
        <button
          onClick={() => setActiveTab('adjustments')}
          className={`px-4 py-2 font-medium transition-colors ${activeTab === 'adjustments'
            ? 'text-primary-600 border-b-2 border-primary-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          {t('suppliers.tabs.debtAdjustments')}
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4 mt-4">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={`${t('buttons.search')} ${t('suppliers.tabs.debtReceipts').toLowerCase()}...`}
          className="w-full"
        />
      </div>

      {/* Supplier Debt Transactions Table */}
      <div className="card">
        <Table
          columns={columns}
          data={filteredAndPaginatedTransactions.data}
          loading={debtReportLoading || propsLoading}
          rowKey="id"
          size="md"
          hoverable
          emptyText={t('suppliers.tabs.noDebtReceipts')}
        />

        {/* Pagination */}
        {filteredAndPaginatedTransactions.total > pageSize && (
          <div className="flex justify-center mt-4">
            <Pagination
              current={currentPage}
              total={filteredAndPaginatedTransactions.total}
              pageSize={pageSize}
              onChange={(page) => setCurrentPage(page)}
              onShowSizeChange={(_current, size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              showSizeChanger
              showTotal={(total, range) =>
                `${t('common.showing')} ${range[0]}-${range[1]} ${t('common.of')} ${total} ${t('suppliers.tabs.debtReceipts')}`
              }
            />
          </div>
        )}
      </div>

      {/* Create Debt Adjustment Modal */}
      {supplier && (
        <>
          <CreateDebtAdjustmentVoucherModal
            isOpen={showCreateDebtAdjustmentModal}
            onClose={() => setShowCreateDebtAdjustmentModal(false)}
            onSuccess={handleRefreshDebtAdjustments}
            onCreated={handleDebtAdjustmentCreated}
            entityType="supplier"
            entityId={supplier.code}
            entityName={supplier.name}
            reportPeriodFrom={dateFrom}
            reportPeriodTo={dateTo}
            currentDebt={debtSummary?.closing_balance || 0}
          />

          {/* Confirm Debt Adjustment Modal */}
          <ConfirmDebtAdjustmentVoucherModal
            isOpen={showConfirmDebtAdjustmentModal}
            onClose={() => {
              setShowConfirmDebtAdjustmentModal(false);
              setCreatedDebtAdjustmentVoucher(null);
            }}
            onSuccess={handleRefreshDebtAdjustments}
            voucher={createdDebtAdjustmentVoucher}
            action="confirm"
            currentDebt={debtSummary?.closing_balance || 0}
          />
        </>
      )}
    </div>
  );
};

export default SupplierDebtTab;
